import { useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { eur, num, shortDate, showMoney } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, PackageCheck, Loader2, Send, Pencil, ArrowUp, ArrowDown, ArrowUpDown, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { SeasonSelect } from "@/components/materials/SeasonSelect";
import { PurchaseForm } from "@/components/materials/PurchaseForm";
import { ReceiveDialog } from "@/components/materials/ReceiveDialog";
import { ConfirmDelete } from "@/components/materials/ConfirmDelete";
import { ListsDialog } from "@/components/materials/ListsDialog";
import { SupplierStats } from "@/components/materials/SupplierStats";
import { ArrivalDate } from "@/components/materials/ArrivalDate";

const todayIso = () => new Date().toISOString().slice(0, 10);

const Arrival = ({ o }) => {
  if (!o.expected_date) return <span className="text-xs text-muted-foreground">da segnare all'arrivo</span>;
  const late = o.expected_date < todayIso();
  return (
    <span className={`text-sm ${late ? "text-rose-400" : ""}`}>
      {shortDate(o.expected_date)}
      {late && <Badge variant="outline" className="ml-2 border-rose-500/50 text-[10px] text-rose-400">in ritardo</Badge>}
    </span>
  );
};

const FabricToSend = ({ o }) => {
  const qc = useQueryClient();
  const send = useMutation({
    mutationFn: async () => (await api.post(`/materials/orders/${o.id}/send-fabric`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["material-orders"] }); qc.invalidateQueries({ queryKey: ["materials"] }); toast.success(`Scaricati ${num(o.fabric_meters)} m di ${o.covered_fabric_name}`); },
    onError: (e) => toast.error(e.response?.data?.detail || "Scarico non riuscito"),
  });
  return (
    <span className="mt-1 flex flex-wrap items-center gap-2 text-[11px]" data-testid={`po-fabric-${o.id}`}>
      <span>Tessuto da mandare: <b className="text-foreground">{num(o.fabric_meters)} m</b> {o.covered_fabric_name} {o.color || ""}</span>
      {o.fabric_sent_at ? <Badge variant="outline" className="border-emerald-500/50 text-[10px] text-emerald-400">mandato il {shortDate(o.fabric_sent_at)}</Badge>
        : <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" disabled={send.isPending} onClick={() => window.confirm(`Scaricare ${num(o.fabric_meters)} m di ${o.covered_fabric_name} ${o.color || ""} dal magazzino?`) && send.mutate()} data-testid={`po-send-fabric-${o.id}`}><Send className="mr-1 h-3 w-3" />Tessuto mandato: scarica</Button>}
    </span>
  );
};

const OrderRow = ({ o, onReceive, onEdit }) => (
  <TableRow data-testid={`po-row-${o.id}`}>
    <TableCell className="font-medium">
      {o.name}<span className="ml-2 text-xs text-muted-foreground">{o.color || ""}</span>
      <span className="block text-[11px] capitalize text-muted-foreground">{o.kind === "tessuti" ? "tessuto" : o.type}{o.notes ? ` · ${o.notes}` : ""}</span>
      {o.fabric_meters > 0 && <FabricToSend o={o} />}
    </TableCell>
    <TableCell className="text-sm">{o.supplier}{o.supplier_code && <span className="block text-[11px] text-muted-foreground">cod. colore {o.supplier_code}</span>}</TableCell>
    <TableCell className="text-right text-sm">{num(o.quantity)} {o.unit}{showMoney() && o.cost_per_unit > 0 && <span className="block text-[11px] text-muted-foreground">{eur(o.quantity * o.cost_per_unit)}</span>}</TableCell>
    <TableCell className="text-sm">{shortDate(o.order_date)}</TableCell>
    <TableCell>
      {o.status === "arrivato" ? (
        <span className="text-sm"><ArrivalDate o={o} /> · {num(o.received_qty)} {o.unit}
          <span className="block text-[11px] text-muted-foreground">in {o.delivery_days} giorni{o.received_qty !== o.quantity ? ` · ordinati ${num(o.quantity)}` : ""}</span>
        </span>
      ) : <Arrival o={o} />}
    </TableCell>
    <TableCell>
      <div className="flex items-center justify-end gap-1">
        {o.status === "in_arrivo" && (
          <>
            <Button size="sm" className="h-7 px-2 text-xs" onClick={() => onReceive(o)} data-testid={`po-receive-${o.id}`}><PackageCheck className="mr-1 h-3.5 w-3.5" />Arrivato</Button>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onEdit(o)} title="Modifica ordine" data-testid={`po-edit-${o.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
          </>
        )}
        <ConfirmDelete title="Eliminare questo ordine?"
          description={`${o.name} ${o.color || ""} · ${num(o.quantity)} ${o.unit} da ${o.supplier}.${o.status === "arrivato" ? " La giacenza in magazzino non cambia: se serve, correggila o elimina la variante nella scheda Tessuti/Accessori." : ""}`}
          url={`/materials/orders/${o.id}`} queryKey={["material-orders"]} success="Ordine eliminato" testid={`po-delete-${o.id}`} />
      </div>
    </TableCell>
  </TableRow>
);

const COLS = [
  ["name", "Articolo", (o) => `${o.name} ${o.color || ""}`.toLowerCase()],
  ["supplier", "Fornitore", (o) => (o.supplier || "").toLowerCase()],
  ["quantity", "Ordinato", (o) => Number(o.quantity) || 0, "text-right"],
  ["order_date", "Data ordine", (o) => o.order_date || ""],
  ["arrival", "Arrivo", (o) => (o.status === "arrivato" ? o.received_date : o.expected_date) || "9999"],
];

export const sortOrders = (orders, sort) => {
  const col = COLS.find((c) => c[0] === sort?.key);
  if (!col) return orders;
  const get = col[2];
  return [...orders].sort((a, b) => { const x = get(a), y = get(b); return (x < y ? -1 : x > y ? 1 : 0) * (sort.dir === "asc" ? 1 : -1); });
};

const SortHead = ({ sort, setSort, testid }) => COLS.map(([k, l, , cls]) => {
  const on = sort?.key === k;
  const Icon = on ? (sort.dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <TableHead key={k} className={cls}>
      <button type="button" onClick={() => setSort(on && sort.dir === "asc" ? { key: k, dir: "desc" } : { key: k, dir: "asc" })}
        className={`inline-flex items-center gap-1 transition-colors hover:text-foreground ${on ? "text-foreground" : ""}`} data-testid={`${testid}-sort-${k}`}>
        {l}<Icon className={`h-3 w-3 ${on ? "" : "opacity-40"}`} />
      </button>
    </TableHead>
  );
});

const OrdersTable = ({ title, orders, count, extra, footer, onReceive, onEdit, empty, testid, sort, setSort }) => (
  <Card className="mb-6 overflow-hidden" data-testid={testid}>
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 px-6 py-4">
      <h3 className="font-display text-lg font-semibold">{title} <span className="text-sm font-normal text-muted-foreground">({count ?? orders.length})</span></h3>
      {extra}
    </div>
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <SortHead sort={sort} setSort={setSort} testid={testid} /><TableHead className="text-right">Azioni</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {orders.map((o) => <OrderRow key={o.id} o={o} onReceive={onReceive} onEdit={onEdit} />)}
        {orders.length === 0 && <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">{empty}</TableCell></TableRow>}
      </TableBody>
    </Table>
    {footer}
  </Card>
);

const PAGE = 30;
const KINDS = [["tessuto", "Tessuti"], ["accessorio", "Accessori"]];

// Arrivati: selettore Tessuti/Accessori, stagione (tessuti), ricerca e pagine da 30 righe
const DoneOrders = ({ orders, materials = [], onReceive }) => {
  const [kind, setKind] = useState("tessuto");
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState(null);
  const [q, setQ] = useState("");
  const { data: seasons } = useQuery({ queryKey: ["material-seasons"], queryFn: async () => (await api.get("/materials/seasons")).data });
  const [seasonPick, setSeason] = useState(null);
  const season = seasonPick || seasons?.current || "tutti";
  const fabric = kind === "tessuto";
  const low = (x) => (x || "").trim().toLowerCase();
  const seasonOf = (o) => (materials.find((m) => m.id === o.material_id)
    || materials.find((m) => m.type === "tessuto" && low(m.name) === low(o.name) && low(m.color) === low(o.color)))?.season;
  const words = low(q).split(/\s+/).filter(Boolean);
  const list = sortOrders(orders.filter((o) => fabric === (o.kind === "tessuti")
    && (!fabric || season === "tutti" || seasonOf(o) === season)
    && words.every((w) => [o.name, o.color, o.supplier, o.supplier_code, o.type, o.notes].some((x) => low(x).includes(w)))), sort);
  const pages = Math.max(1, Math.ceil(list.length / PAGE));
  const p = Math.min(page, pages - 1);
  const extra = (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} placeholder="Cerca articolo, colore, fornitore..." className="h-9 w-72 pl-9" data-testid="po-done-search" />
      </div>
      {fabric && <SeasonSelect value={season} options={seasons?.options || []} current={seasons?.current} onChange={(s) => { setSeason(s); setPage(0); }} testid="po-done-season" />}
    <div className="flex gap-1 rounded-md bg-secondary p-1 text-xs">
      {KINDS.map(([k, l]) => (
        <button key={k} type="button" onClick={() => { setKind(k); setPage(0); }} data-testid={`po-done-kind-${k}`}
          className={`rounded px-3 py-1 transition-colors ${kind === k ? "bg-background font-medium text-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
      ))}
    </div>
    </div>
  );
  const footer = pages > 1 && (
    <div className="flex items-center justify-end gap-2 border-t border-border/60 px-6 py-3 text-sm">
      <Button size="sm" variant="outline" disabled={p === 0} onClick={() => setPage(p - 1)} data-testid="po-done-prev">Precedente</Button>
      <span className="text-muted-foreground" data-testid="po-done-page">Pagina {p + 1} di {pages}</span>
      <Button size="sm" variant="outline" disabled={p >= pages - 1} onClick={() => setPage(p + 1)} data-testid="po-done-next">Successiva</Button>
    </div>
  );
  return <OrdersTable title="Arrivati di recente" orders={list.slice(p * PAGE, (p + 1) * PAGE)} count={list.length} extra={extra} footer={footer}
    sort={sort} setSort={(s) => { setSort(s); setPage(0); }}
    onReceive={onReceive} empty={words.length ? "Nessun arrivo corrisponde alla ricerca." : fabric ? "Nessun tessuto arrivato in questa stagione." : "Nessun accessorio arrivato."} testid="po-done-table" />;
};

export const PurchaseOrders = ({ materials, actionsEl }) => {
  const [form, setForm] = useState(false);
  const [receiving, setReceiving] = useState(null);
  const [editing, setEditing] = useState(null);
  const [openSort, setOpenSort] = useState(null);
  const { data: orders = [], isLoading } = useQuery({ queryKey: ["material-orders"], queryFn: async () => (await api.get("/materials/orders")).data });
  const { data: lists = {}, isSuccess: listsReady } = useQuery({ queryKey: ["material-lists"], queryFn: async () => (await api.get("/materials/lists")).data });
  const open = orders.filter((o) => o.status === "in_arrivo").sort((a, b) => (a.expected_date || "9").localeCompare(b.expected_date || "9"));
  const done = orders.filter((o) => o.status === "arrivato");
  if (isLoading) return <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  return (
    <div data-testid="purchase-orders">
      {actionsEl && createPortal(
        <>
          {listsReady && <ListsDialog lists={lists} ready={listsReady} columns={["fabric_types", "suppliers", "colors", "accessory_types"]} />}
          <Button onClick={() => setForm(true)} data-testid="new-purchase-order-btn"><Plus className="mr-2 h-4 w-4" />Nuovo ordine</Button>
        </>,
        actionsEl,
      )}
      <OrdersTable title="In arrivo" orders={sortOrders(open, openSort)} sort={openSort} setSort={setOpenSort} onReceive={setReceiving} onEdit={setEditing} empty="Nessun ordine in arrivo." testid="po-open-table" />
      <DoneOrders orders={done} materials={materials} onReceive={setReceiving} />
      <SupplierStats orders={orders} />
      {form && <PurchaseForm open onClose={() => setForm(false)} materials={materials} lists={lists} />}
      {editing && <PurchaseForm open order={editing} onClose={() => setEditing(null)} materials={materials} lists={lists} />}
      {receiving && <ReceiveDialog order={receiving} onClose={() => setReceiving(null)} />}
    </div>
  );
};
